import { gql } from "@apollo/client"
import { AcceptAll, DraftReason, EditEnding, UnlockDraft } from "event-definitions"
import { beep } from "lib/beep"
import { Bound } from "lib/@components/binding/Bound"
import { delay } from "lib/delay"
import { Loader } from "lib/@components/Loader"
import { useUndo } from "lib/undo"
import { useRefresh } from "lib/@hooks/useRefresh"
import { initialize } from "library/behaviours/index.mjs"
import { uniqueId } from "library/database/split-id.mjs"
import { raiseOnce } from "library/local-events/index.mjs"
import { useCallback, useEffect, useRef, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { states } from "routes/schedule/edit/schedule-edit.runtime"

import { useScheduleForEditing } from "routes/schedule/lib/use-schedule-for-editing"
import { mutate } from "lib/graphql/mutate"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import ScheduleDisplay from "routes/schedule/scheduleDisplay"
import { SaveIndicator } from "lib/@components/save-indicator/save-indicator"
import { SaveWarning } from "routes/schedule/edit/save-warning"
import { Lock } from "minimals-template/components/lock/Lock"
import { stillEditing } from "minimals-template/components/lock/controller/still-editing"
import { ErrorPage } from "lib/@components/error-page"
import { getScheduleRecordId } from "library/get-unique-id-from-tree.mjs"
import { useOfflineSave } from "lib/@offline/use-offline-save"
import { registerSaveFunction, unregisterSaveFunction } from "lib/@offline/draft-sync-manager"
import { clearLocalDraft } from "lib/@offline/local-draft-store"
import noop from "lib/noop"
import { getVersionFromSaveResult } from "routes/schedule/edit/version-from-save-result"
import { EditBackButton } from "routes/schedule/edit/edit-back-button"
import { useMacros } from "authoring/routes/macro/hooks/use-macros"
import { ScheduleMacroAutocomplete } from "authoring/routes/project/schedule-editor/schedule-macro-autocomplete"

export function ScheduleEdit(config) {
    const { id } = useParams()
    const code = getScheduleRecordId(id)
    const definition = useScheduleForEditing(code, true)
    const loading = !definition
    const error = definition instanceof Error ? definition : undefined

    useEffect(() => {
        raiseOnce("CurrentSchedule", id)
    }, [id])

    if (loading) return <Loader />
    if (error && error?.message !== "Cannot lock draft for editing") return <ErrorPage error={error?.message} />

    return (
        <Lock
            id={code}
            breadcrumb={"Schedule Locked"}
            message={"This schedule is currently locked because the person indicated below is making changes to it."}
        >
            <ScheduleEditContents id={id} code={code} {...config} definition={definition} />
        </Lock>
    )
}

export function ScheduleEditContents({ id, code, definition, preview }) {
    const location = useLocation()
    const draftKey = `custom:${code}`
    const isUnlocked = useRef(false)
    UnlockDraft.useEvent(() => {
        isUnlocked.current = true
        clearLocalDraft(draftKey).catch(noop)
    }, [isUnlocked, draftKey])
    const activeSchedule = useRef()
    const editRefresh = useRefresh()
    const { macros: allMacros, macroMap } = useMacros()
    const isAuthoringRoute = location.pathname.startsWith("/authoring")
    const reasonRef = useRef("")
    DraftReason.useEvent(
        (r) => {
            reasonRef.current = r
        },
        [reasonRef]
    )
    const approveAllStatus = useRef({})
    AcceptAll.useEvent((accept) => {
        approveAllStatus.current = { approveAll: accept }
    })

    // When a queued local draft is restored, use it as the initialTarget instead
    // of the server definition. This reinitialises the undo buffer cleanly rather
    // than mutating the existing target in-place (which would corrupt undo history).
    const [restoredDraft, setRestoredDraft] = useState(null)

    const draftVersionRef = useRef(definition?._draftVersion ?? 0)

    const [schedule, { onChange, ...props }] = useUndo({ initialTarget: restoredDraft ?? definition })
    activeSchedule.current = schedule

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const saveToServer = useCallback(saveToServerFn, [code])

    const { onChange: triggerSave, status: saveStatus } = useOfflineSave({
        id: "schedule",
        save: saveToServer,
        target: schedule,
        draftKey,
        editorType: "custom-schedule",
        serverVersion: draftVersionRef.current,
        wait: 1000,
        saveContext: {
            code,
            expectedVersion: draftVersionRef.current,
            writeOrderKey: `updateDraft:${code}`,
        },
        onRestore: handleRestoredDraft,
        preparePayload: prepareSchedulePayload,
    })

    const saveStatusRef = useRef(saveStatus)
    saveStatusRef.current = saveStatus
    const triggerSaveRef = useRef(triggerSave)
    triggerSaveRef.current = triggerSave

    useEffect(() => {
        registerSaveFunction(draftKey, saveToServer)
        return () => unregisterSaveFunction(draftKey)
    }, [draftKey, saveToServer])

    useEffect(addBeforeUnloadListener, [])

    useEffect(() => {
        const timerId = setInterval(() => {
            stillEditing(id)
        }, 15000)
        return async () => {
            clearInterval(timerId)
            EditEnding.raise()
            await delay(50)
            triggerSaveRef.current?.flush?.()
            await unlockDraftOnServer()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    initialize(schedule)
    if (schedule) {
        schedule.id = uniqueId(schedule._id)
    }
    return (
        <Bound
            editMode={!preview}
            {...props}
            editRefresh={editRefresh}
            onChange={notifyChanged}
            target={schedule}
            schedule={schedule}
            immediateSave={() => triggerSave.flush()}
            draftReason={reasonRef.current}
            saveStatus={saveStatus}
            macroMap={macroMap}
        >
            <SaveIndicator id="schedule" />
            <SaveWarning id="schedule" />
            {!preview && <EditBackButton />}
            {preview ? !!schedule && <ScheduleDisplay /> : !!schedule && states(schedule)}
            {isAuthoringRoute && <ScheduleMacroAutocomplete macros={allMacros} enabled={!preview} />}
        </Bound>
    )

    function addBeforeUnloadListener() {
        window.addEventListener("beforeunload", onBeforeUnload)
        return () => window.removeEventListener("beforeunload", onBeforeUnload)
    }

    function onBeforeUnload(event) {
        const s = saveStatusRef.current
        if (s && s !== "saved" && s !== "idle" && s !== "queued") {
            event.preventDefault()
            event.returnValue = ""
        }
    }

    function handleRestoredDraft(restoredData, draft) {
        const restoredVersion = draft?.saveContext?.expectedVersion ?? draft?.serverVersion
        if (restoredVersion != null) {
            draftVersionRef.current = restoredVersion
        }
        setRestoredDraft(restoredData)
    }

    function prepareSchedulePayload(payload) {
        const prepared = { ...payload }
        prepared.legislation ??= []
        prepared.legislation = prepared.legislation.unique()
        if (reasonRef.current) {
            prepared.draftReason = reasonRef.current
        }
        prepared.approveAllStatus = approveAllStatus.current
        return prepared
    }

    async function saveToServerFn(payload, options = {}) {
        if (isUnlocked.current) return { skipped: true }

        // preparePayload already enriches the payload before it reaches
        // this function, so we only re-apply fields whose refs may hold
        // newer values than what was captured at queue time.
        const toSave = { ...payload }
        toSave.legislation ??= []
        toSave.legislation = toSave.legislation.unique()
        if (reasonRef.current) {
            toSave.draftReason = reasonRef.current
        }
        toSave.approveAllStatus = approveAllStatus.current ?? toSave.approveAllStatus

        const result = await mutate(
            gql`
                mutation updateDraft(
                    $id: String!
                    $content: JSONObject!
                    $expectedVersion: Int
                    $forceOverwrite: Boolean
                ) {
                    writeContent(
                        id: $id
                        content: $content
                        expectedVersion: $expectedVersion
                        forceOverwrite: $forceOverwrite
                    )
                }
            `,
            {
                content: toSave,
                id: code,
                expectedVersion: draftVersionRef.current,
                forceOverwrite: options.forceOverwrite,
            },
            { returns: "writeContent" }
        )

        const nextDraftVersion = getVersionFromSaveResult(result, "draftVersion")
        const saveAccepted = !result?.conflict && !result?.stale && !result?.skipped && result?.success !== false
        if (saveAccepted && nextDraftVersion != null) {
            draftVersionRef.current = nextDraftVersion
        }
        // Only clear the reason once the server has actually persisted the
        // save, so retries after conflict/stale/failure still include it.
        if (saveAccepted) {
            reasonRef.current = null
        }
        return result
    }

    async function unlockDraftOnServer() {
        if (isUnlocked.current) return
        isUnlocked.current = true

        const toSave = { ...activeSchedule.current }
        toSave.legislation ??= []
        toSave.legislation = toSave.legislation.unique()
        if (reasonRef.current) {
            toSave.draftReason = reasonRef.current
        }
        toSave.approveAllStatus = approveAllStatus.current

        try {
            await mutate(
                gql`
                    mutation unlockDraft($id: String!, $content: JSONObject!, $unlock: Boolean) {
                        writeContent(id: $id, content: $content, unlock: $unlock)
                    }
                `,
                {
                    unlock: true,
                    content: toSave,
                    id: code,
                },
                { fetchPolicy: "network-only", returns: "writeContent" }
            )
            await clearLocalDraft(draftKey).catch(noop)
        } catch (error) {
            console.error("Failed to unlock draft:", error)
            if (error.message === "Cannot lock draft for editing") {
                // Draft is already locked/published by someone else — our local copy is stale
                await clearLocalDraft(draftKey).catch(noop)
            } else {
                await beep()
                errorSnackbar("Could not save document! Your changes were not saved.", { persist: true })
            }
        }
    }

    function notifyChanged(...params) {
        onChange(...params)
        triggerSave()
    }
}
